import { Time } from "@internationalized/date";
import { useTranslations } from "next-intl";
import { Fragment, use, type ReactNode } from "react";
import { useDateFormatter } from "react-aria";

import { TIMEZONE } from "@trip/constants";
import { useTimeFormatter } from "@trip/react-hooks";

import { getPlanChoices } from "~/app/[locale]/(default)/tours/[slug]/[id]/_components/lib";
import { RemoveCartLine } from "~/app/[locale]/(default)/tours/[slug]/[id]/_components/remove-cart-line";
import { AngleLeftSmallIcon } from "~/components/icons/angle-left-small";
import { LoadingSpinner } from "~/components/loading-spinner";
import { StartTimesPicker } from "~/components/start-times";
import { TourPassengerCategories } from "~/components/tour-passenger-categories";
import { TourPlans } from "~/components/tour-plans";
import { H4, SunsetCaption } from "~/components/typography";
import { Button } from "~/components/ui/button";
import { DialogAccordion } from "~/components/ui/dialog-accordion";
import { type ProductId } from "~/lib/tours";
import { useCart } from "~/lib/use-cart";
import { useCurrencyFormatter } from "~/lib/use-currency";
import { type TripTour } from "~/lib/use-trip-tour";
import { useRouter } from "~/navigation";
import { trpcReact } from "~/trpc/client";

import { type Day } from "../_lib/trip";
import { TripContext } from "../_lib/trip-context";
import { TourBookingAddToBag } from "./tour-booking-add-to-bag";

type DatoTour = Day["tours"][0]["tour"];

function Header({
  count,
  amount,
  dayNumber,
  setPage,
  isFetching,
}: {
  amount?: number;
  count: number;
  dayNumber: number;
  setPage: (value: "info") => void;
  isFetching: boolean;
}) {
  const t = useTranslations("TripDetails");
  const formatCurrency = useCurrencyFormatter();
  const formattedAmount = amount ? formatCurrency(amount) : null;
  return (
    <div className="mb-4 flex w-full items-center justify-between">
      <Button
        onClick={(event) => {
          event.preventDefault();
          setPage("info");
        }}
        size="circle"
        variant="default"
      >
        <AngleLeftSmallIcon />
      </Button>
      <div className="relative min-h-6 text-right">
        <SunsetCaption>{t("dayNumber", { number: dayNumber })}</SunsetCaption>
        <div className="text-xs font-medium">
          {isFetching ? (
            <>
              <LoadingSpinner className="absolute right-0 size-3 opacity-50" />{" "}
              &nbsp;
            </>
          ) : formattedAmount ? (
            <>
              {t.rich("priceForPeople", {
                amount: (chunks) => <strong>{chunks}</strong>,
                count,
                formattedAmount,
              })}
            </>
          ) : (
            <>&nbsp;</>
          )}
        </div>
      </div>
    </div>
  );
}

function Accordion({
  startTime,
  totalCount,
  startTimes,
  passengers,
  cancellationPolicy,
  planLabel,
  plans,
}: {
  startTime?: TripTour["startTime"];
  passengers: ReactNode;
  startTimes: ReactNode;
  planLabel: ReactNode;
  plans: ReactNode;
  cancellationPolicy?: ReactNode;
  totalCount: number;
}) {
  const t = useTranslations("TourDetails");
  const formatTime = useTimeFormatter();

  return (
    <DialogAccordion
      sections={[
        plans
          ? {
              children: (
                <div className="p-4">
                  <H4 className="mb-3">{t("plans")}</H4>
                  <div className="mb-2 text-sm">{plans}</div>
                </div>
              ),
              cta: planLabel,
              label: t("plan"),
              value: "plans",
            }
          : undefined,
        startTimes
          ? {
              children: (
                <div className="p-4">
                  <H4 className="mb-3">{t("when")}</H4>
                  <div>{startTimes}</div>
                </div>
              ),

              cta: startTime
                ? formatTime(new Time(startTime.hour, startTime.minute))
                : undefined,
              label: t("when"),
              value: "startTimes",
            }
          : undefined,
        {
          children: (
            <div className="p-4">
              <H4 className="mb-3">{t("whosComing")}</H4>
              <div>{passengers}</div>
            </div>
          ),
          cta: t("passengerCount", { count: totalCount }),
          label: t("who"),
          value: "passengers",
        },
        cancellationPolicy
          ? {
              children: (
                <div className="p-4">
                  <H4 className="mb-3">{t("cancellationPolicy")}</H4>
                  <div className="mb-2 text-sm">{cancellationPolicy}</div>
                </div>
              ),
              cta: "",
              label: t("cancellationPolicy"),
              value: "cancellationPolicy",
            }
          : undefined,
      ]}
    />
  );
}

export function TourBooking({
  choice,
  totalPassengers,
  dispatch,
  config,
  passengerCategories,
  cartLine,
  planId,
  errors,
  isFetching,
  startTime,
  startTimes,
  setPage,
  dayNumber,
  datoDayId,
  datoTour,
  productId,
}: {
  date: Date;
  setPage: (value: "info") => void;
  dayNumber: number;
  datoDayId: string;
  datoTour: DatoTour;
  productId: ProductId;
} & TripTour) {
  const t = useTranslations("TourDetails");
  const trip = use(TripContext);
  if (!trip) {
    throw new Error("TripContext is not available");
  }
  const { addTourCartLine } = useCart();
  const router = useRouter();
  const datoTripActionMutation = trpcReact.cart.datoTripAction.useMutation();
  const dateFormatter = useDateFormatter({
    dateStyle: "medium",
    timeZone: TIMEZONE,
  });
  const planChoices = getPlanChoices({
    datoPlans: datoTour.plans,
    formatDate: (date: Date) => dateFormatter.format(date),
    plans: config.plans,
    t,
  });

  return (
    <div className="flex h-full flex-col gap-2">
      <Header
        count={totalPassengers}
        dayNumber={dayNumber}
        setPage={setPage}
        amount={choice?.amount}
        isFetching={isFetching}
      />
      <div className="grow">
        <Accordion
          planLabel={config.plans.find(({ id }) => id === planId)?.name}
          startTime={startTime}
          totalCount={totalPassengers}
          startTimes={
            startTimes.length > 0 ? (
              <StartTimesPicker
                value={startTime?.id ?? null}
                onChange={(value) => {
                  const startTime = startTimes.find(({ id }) => value === id);
                  if (startTime) {
                    dispatch({
                      type: "update-start-time",
                      starTime: {
                        hour: startTime.hour ?? 0,
                        minute: startTime.minute ?? 0,
                      },
                    });
                  }
                }}
                startTimes={startTimes}
              />
            ) : null
          }
          passengers={
            <div className="flex flex-col gap-4">
              <TourPassengerCategories
                onChange={({ id, count }) => {
                  dispatch({
                    type: "update-passenger-count",
                    config,
                    count,
                    id,
                  });
                }}
                passengerCategories={passengerCategories}
                platform={config.platform}
              />
              {cartLine ? <RemoveCartLine cartLine={cartLine} /> : null}
            </div>
          }
          plans={
            config.plans.length > 1 ? (
              <TourPlans
                plan={config.plan}
                choices={planChoices}
                setPlanId={(id) => {
                  dispatch({ type: "update-plan", id });
                }}
              />
            ) : null
          }
          cancellationPolicy={
            config.cancellationPolicy.length > 0 ? (
              <div className="mt-4 text-center text-xs text-neutral-500">
                {config.cancellationPolicy.map((policy) => (
                  <Fragment key={policy}>{policy}</Fragment>
                ))}
              </div>
            ) : null
          }
        />
      </div>
      <div>
        <div className="relative min-h-8 w-full">
          {errors.length > 0 ? (
            <div className="text-japan text-center">{errors[0]}</div>
          ) : choice ? (
            <TourBookingAddToBag
              onClick={() => {
                const addTour = (tripLineItemId: string) =>
                  addTourCartLine.mutateAsync({
                    date: choice.date,
                    datoTourId: datoTour.id,
                    passengerCategories,
                    planId,
                    productId,
                    startTimeId: startTime?.id,
                    datoDayId,
                    tripLineItemId,
                    tripFields: {
                      ...trip.passengers,
                      date: trip.date,
                    },
                  });

                if (!trip.lineItem) {
                  // Fork the dato template trip first, then add the tour
                  void datoTripActionMutation
                    .mutateAsync({
                      type: "FORK",
                      datoTripId: trip.datoTrip.id,
                    })
                    .then(async (tripLineItemId) => {
                      await addTour(tripLineItemId);
                      router.refresh(); // Refresh to update trip pricing after tour addition
                    })
                    .then(() => {
                      setPage("info");
                    });
                } else {
                  // Trip is already forked, just add the tour
                  void addTour(trip.lineItem.id).then(() => {
                    router.refresh(); // Refresh to update trip pricing after tour addition
                    setPage("info");
                  });
                }
              }}
              datoTourId={datoTour.id}
              loading={
                isFetching ||
                addTourCartLine.isPending ||
                datoTripActionMutation.isPending
              }
              productId={productId}
            />
          ) : isFetching ? (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <LoadingSpinner />
            </div>
          ) : (
            t("noAvailabilityFound")
          )}
        </div>
      </div>
    </div>
  );
}
